import React, { useState, useEffect } from 'react';
import { 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Box, 
    Typography,
    IconButton,
    Divider,
    Tabs,
    Tab,
    Card,
    CardContent,
    Chip,
    Tooltip,
    Avatar,
    AvatarGroup,
    Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
    Add as AddIcon, 
    Close as CloseIcon,
    Category as CategoryIcon,
    Spa as ServiceIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Store as StoreIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Person as PersonIcon,
    Translate as TranslateIcon
} from '@mui/icons-material';
import { 
    useSalonsCategoriesListQuery,
    useServicesSalonListQuery,
    useTeamsTeamMembersSalonMembersRetrieveQuery
} from '../../../services/GLOWClient';
import AddCategory from './category/addCategory';
import AddService from './service/addService';
import EditService from './service/editService';
import UpdateCategory from './category/updateCategory';
import DeleteCategory from './category/deleteCategory';
import DeleteService from './service/deleteService';
import Service from './service/service';
import TranslationValidationModal from './TranslationValidationModal';
import useSequentialDialogs from '../../../hooks/useSequentialDialogs';
import theme from '../../../theme';

const ServiceCategorySectionSequential = ({
    onSuccess,
    onQuit,
    selectedSalonId = null 
}) => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('categories'); // 'categories' or 'services'
    const [createdCategories, setCreatedCategories] = useState([]);
    const [createdServices, setCreatedServices] = useState([]);
    const [currentCategoryForService, setCurrentCategoryForService] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
    const [selectedCategoryForDelete, setSelectedCategoryForDelete] = useState(null);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedServiceForDelete, setSelectedServiceForDelete] = useState(null);
    const [isDeleteServiceDialogOpen, setIsDeleteServiceDialogOpen] = useState(false);
    const [selectedServiceForEdit, setSelectedServiceForEdit] = useState(null);
    const [isEditServiceDialogOpen, setIsEditServiceDialogOpen] = useState(false);
    const [serviceTranslationModalOpen, setServiceTranslationModalOpen] = useState(false);
    const [serviceTranslationModalLoading, setServiceTranslationModalLoading] = useState(false);
    const [translationService, setTranslationService] = useState(null);
    const [serviceTranslations, setServiceTranslations] = useState(null);
    const [categoryTranslationModalOpen, setCategoryTranslationModalOpen] = useState(false);
    const [categoryTranslationModalLoading, setCategoryTranslationModalLoading] = useState(false);
    const [translationCategory, setTranslationCategory] = useState(null);
    const [categoryTranslations, setCategoryTranslations] = useState(null);
    const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();
    
    // Fetch existing data from backend
    const { data: categoriesData, refetch: refetchCategories } = useSalonsCategoriesListQuery(
        selectedSalonId ? { salonId: selectedSalonId, language: i18n.language } : {},
        { skip: !selectedSalonId }
    );
    
    const { data: servicesData, refetch: refetchServices } = useServicesSalonListQuery(
        selectedSalonId ? { salonId: selectedSalonId, language: i18n.language } : {},
        { skip: !selectedSalonId }
    );

    const getTranslatedField = React.useCallback((item, field) => {
        const translationsKey = `${field}_translations`;
        const localizedKey = `localized_${field}`;
        const translations = item?.[translationsKey] || {};

        if (translations[currentLanguage]) {
            return translations[currentLanguage];
        }

        if (item?.[localizedKey]) {
            return item[localizedKey];
        }

        for (const fallbackLanguage of ['fr', 'en', 'ar']) {
            if (translations[fallbackLanguage]) {
                return translations[fallbackLanguage];
            }
        }

        return item?.[field] || '';
    }, [currentLanguage]);
    
    // Combine existing and newly created data (avoid temporary duplicates after refetch)
    const baseCategories = Array.isArray(categoriesData)
        ? categoriesData
        : (categoriesData?.results || []);
    const baseCategoryIds = new Set(
        baseCategories
            .map((c) => c?.category_id ?? c?.id ?? c?.pk)
            .filter((id) => id !== null && id !== undefined)
            .map((id) => Number(id))
    );
    const baseCategoryKeys = new Set(
        baseCategories
            .map((c) => {
                const name = (c?.name || '').trim().toLowerCase();
                const salon = c?.salon ?? null;
                return name ? `${salon ?? 'null'}::${name}` : null;
            })
            .filter(Boolean)
    );
    const allCategories = [
        ...baseCategories,
        ...createdCategories.filter((c) => {
            const id = c?.category_id ?? c?.id ?? c?.pk;
            if (id === null || id === undefined) {
                const name = (c?.name || '').trim().toLowerCase();
                const salon = c?.salon ?? selectedSalonId ?? null;
                if (!name) return true;
                return !baseCategoryKeys.has(`${salon ?? 'null'}::${name}`);
            }
            return !baseCategoryIds.has(Number(id));
        })
    ];
    
    const baseServices = Array.isArray(servicesData)
        ? servicesData
        : (servicesData?.results || []);
    const baseServiceIds = new Set(
        baseServices
            .map((s) => s?.service_id ?? s?.id ?? s?.pk)
            .filter((id) => id !== null && id !== undefined)
            .map((id) => Number(id))
    );
    const baseServiceKeys = new Set(
        baseServices
            .map((s) => {
                const name = (s?.name || '').trim().toLowerCase();
                const category = s?.category ?? s?.category_id ?? s?.category_details?.category_id ?? null;
                return name ? `${category ?? 'null'}::${name}` : null;
            })
            .filter(Boolean)
    );
    const allServices = [
        ...baseServices,
        ...createdServices.filter((s) => {
            const id = s?.service_id ?? s?.id ?? s?.pk;
            if (id === null || id === undefined) {
                const name = (s?.name || '').trim().toLowerCase();
                const category = s?.category ?? s?.category_id ?? s?.category_details?.category_id ?? null;
                if (!name) return true;
                return !baseServiceKeys.has(`${category ?? 'null'}::${name}`);
            }
            return !baseServiceIds.has(Number(id));
        })
    ];

    useEffect(() => {
        if (!selectedSalonId) return;

        const normalizeCategoryId = (c) => c?.category_id ?? c?.id ?? c?.pk;
        const normalizeServiceId = (s) => s?.service_id ?? s?.id ?? s?.pk;

        const reportDupes = (label, items, getId, getName) => {
            const byId = new Map();
            const byName = new Map();

            items.forEach((item, idx) => {
                const rawId = getId(item);
                const idKey = rawId === null || rawId === undefined ? null : String(rawId);
                const rawName = (getName(item) || '').trim().toLowerCase();

                if (idKey !== null) {
                    const arr = byId.get(idKey) || [];
                    arr.push({ idx, id: rawId, name: getName(item) });
                    byId.set(idKey, arr);
                }
                if (rawName) {
                    const arr = byName.get(rawName) || [];
                    arr.push({ idx, id: rawId, name: getName(item) });
                    byName.set(rawName, arr);
                }
            });

            const idDupes = Array.from(byId.entries()).filter(([, arr]) => arr.length > 1);
            const nameDupes = Array.from(byName.entries()).filter(([, arr]) => arr.length > 1);

            if (idDupes.length || nameDupes.length) {
                console.log(`[${label}] duplicates detected`, {
                    count: items.length,
                    idDupes,
                    nameDupes,
                });
            }
        };

        console.log('[SalonSetup] categories snapshot', {
            selectedSalonId,
            baseCategoriesCount: baseCategories.length,
            createdCategoriesCount: createdCategories.length,
            allCategoriesCount: allCategories.length,
        });
        console.log('[SalonSetup] services snapshot', {
            selectedSalonId,
            baseServicesCount: baseServices.length,
            createdServicesCount: createdServices.length,
            allServicesCount: allServices.length,
        });

        reportDupes(
            'SalonSetup.categories',
            allCategories,
            normalizeCategoryId,
            (c) => c?.name
        );
        reportDupes(
            'SalonSetup.services',
            allServices,
            normalizeServiceId,
            (s) => s?.name
        );
    }, [
        selectedSalonId,
        baseCategories,
        createdCategories,
        allCategories,
        baseServices,
        createdServices,
        allServices,
    ]);

    // Fetch team members for the selected salon to detect services without employees
    const { data: salonMembersData } = useTeamsTeamMembersSalonMembersRetrieveQuery(
        selectedSalonId ? { salon: selectedSalonId } : {},
        { skip: !selectedSalonId }
    );

    // Normalize team members list
    const salonMembers = React.useMemo(() => {
        if (!salonMembersData) return [];
        if (Array.isArray(salonMembersData)) return salonMembersData;
        return salonMembersData.results || [];
    }, [salonMembersData]);

    // Map service ID -> employés qui réalisent la prestation
    const serviceEmployeesMap = React.useMemo(() => {
        if (!salonMembers || salonMembers.length === 0) return {};
        const map = {};
        salonMembers.forEach(member => {
            const serviceIds = Array.isArray(member.service_ids) ? member.service_ids : [];
            serviceIds.forEach(id => {
                if (id === null || id === undefined) return;
                const key = Number(id);
                if (!map[key]) {
                    map[key] = [];
                }
                map[key].push(member);
            });
        });
        return map;
    }, [salonMembers]);

    // Compute services that are not assigned to any employee for this salon
    const unassignedServices = React.useMemo(() => {
        if (!allServices || allServices.length === 0) return [];
        // If no members, all services are effectively unassigned
        if (!salonMembers || salonMembers.length === 0) return allServices;
        
        const assignedServiceIds = new Set();
        salonMembers.forEach(member => {
            if (Array.isArray(member.service_ids)) {
                member.service_ids.forEach(id => {
                    if (id !== null && id !== undefined) {
                        assignedServiceIds.add(Number(id));
                    }
                });
            }
        });
        
        return allServices.filter(service => {
            const id = service.service_id || service.id;
            if (id === null || id === undefined) return false;
            return !assignedServiceIds.has(Number(id));
        });
    }, [allServices, salonMembers]);

    const hasUnassignedServices = unassignedServices.length > 0;

    const { 
        currentDialog, 
        openDialog, 
        closeDialog, 
        closeAllDialogs 
    } = useSequentialDialogs();

    const tabs = [t('pro_service_category.categorized'), t('pro_service_category.uncategorized')];

    // Reset state on mount
    useEffect(() => {
        setActiveTab(0);
        setCreatedCategories([]);
        setCreatedServices([]);
        setCurrentCategoryForService(null);
        closeAllDialogs();
    }, [closeAllDialogs]);
    
    // Refetch data when new items are created
    useEffect(() => {
        if (selectedSalonId) {
            refetchCategories();
            refetchServices();
        }
    }, [selectedSalonId, refetchCategories, refetchServices]);

    const handleCategorySuccess = (message, categoryData) => {
        if (categoryData) {
            const categoryWithId = {
                ...categoryData,
                category_id: categoryData.category_id || categoryData.id || categoryData.pk
            };
            setCreatedCategories((prev) => {
                const newId = categoryWithId?.category_id;
                if (newId === null || newId === undefined) {
                    return [...prev, categoryWithId];
                }
                const exists = prev.some((c) => Number(c?.category_id ?? c?.id ?? c?.pk) === Number(newId));
                return exists ? prev : [...prev, categoryWithId];
            });
        }
        
        // Refetch backend data
        refetchCategories();
        
        closeDialog(); // Close the category dialog
        
        if (onSuccess) {
            onSuccess(message || t('pro_service_category.category_created_success'));
        }
    };

    const handleServiceSuccess = (messageOrServiceData, maybeServiceData) => {
        const serviceData = maybeServiceData || (
            messageOrServiceData && typeof messageOrServiceData === 'object' ? messageOrServiceData : null
        );
        const successMessage = typeof messageOrServiceData === 'string'
            ? messageOrServiceData
            : t('pro_service_category.service_created_success');

        if (serviceData) {
            const serviceWithId = {
                ...serviceData,
                service_id: serviceData.service_id || serviceData.id || serviceData.pk
            };
            setCreatedServices((prev) => {
                const newId = serviceWithId?.service_id;
                if (newId === null || newId === undefined) {
                    return [...prev, serviceWithId];
                }
                const exists = prev.some((s) => Number(s?.service_id ?? s?.id ?? s?.pk) === Number(newId));
                return exists ? prev : [...prev, serviceWithId];
            });
        }
        
        // Refetch backend data
        refetchServices();
        
        closeDialog(); // Close the service dialog
        
        if (onSuccess) {
            onSuccess(successMessage, serviceData);
        }
    };

    const handleOpenCategoryDialog = () => {
        openDialog({
            type: 'category',
            props: {
                open: true,
                onClose: closeDialog,
                onSuccess: handleCategorySuccess,
                selectedSalonId,
                isCreationMode: true
            }
        });
    };

    const handleEditService = (service) => {
        setSelectedServiceForEdit(service);
        setIsEditServiceDialogOpen(true);
    };

    const handleOpenServiceDialog = (category = null) => {
        setCurrentCategoryForService(category);
        openDialog({
            type: 'service',
            props: {
                open: true,
                onClose: closeDialog,
                onSuccess: handleServiceSuccess,
                salonId: selectedSalonId,
                isCreationMode: true,
                categories: allCategories,
                preselectedCategory: category
            }
        });
    };

    const handleEditCategory = (category) => {
        setSelectedCategoryForEdit(category);
        setIsUpdateDialogOpen(true);
    };

    const handleDeleteCategory = (category) => {
        setSelectedCategoryForDelete(category);
        setIsDeleteDialogOpen(true);
    };

    const handleUpdateSuccess = () => {
        setIsUpdateDialogOpen(false);
        setSelectedCategoryForEdit(null);
        refetchCategories();
        if (onSuccess) {
            onSuccess(t('pro_service_category.category_updated_success'));
        }
    };

    const handleDeleteSuccess = () => {
        setIsDeleteDialogOpen(false);
        setSelectedCategoryForDelete(null);
        refetchCategories();
        if (onSuccess) {
            onSuccess(t('pro_service_category.category_deleted_success'));
        }
    };

    const handleDeleteServiceSuccess = (deletedService, error) => {
        setIsDeleteServiceDialogOpen(false);
        setSelectedServiceForDelete(null);
        
        if (deletedService) {
            refetchServices();
            if (onSuccess) {
                onSuccess(t('pro_service_category.service_deleted_success', { name: getTranslatedField(deletedService, 'name') }));
            }
        } else if (error) {
            console.error('Failed to delete service:', error);
            if (onSuccess) {
                onSuccess(t('pro_service_category.service_delete_error'));
            }
        }
    };

    const handleEditServiceSuccess = (updatedService, error) => {
        setIsEditServiceDialogOpen(false);
        setSelectedServiceForEdit(null);

        if (updatedService) {
            refetchServices();
            if (onSuccess) {
                onSuccess(t('pro_service_category.service_updated_success', { name: getTranslatedField(updatedService, 'name') }));
            }
        } else if (error) {
            console.error('Failed to update service:', error);
            if (onSuccess) {
                onSuccess(t('pro_service_category.service_update_error'));
            }
        } else {
            // Category or other update without explicit service payload
            refetchServices();
        }
    };

    const handleOpenTranslations = async (service) => {
        setTranslationService(service);
        setServiceTranslationModalOpen(true);
        setServiceTranslationModalLoading(true);
        setServiceTranslations(null);

        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            const serviceId = service.service_id || service.id;
            const response = await fetch(`/api/services/${serviceId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setServiceTranslations({
                    name: data.name_translations || {},
                    description: data.description_translations || {},
                });
            }
        } catch (e) {
            console.warn('[service translations] fetch error', e);
        } finally {
            setServiceTranslationModalLoading(false);
        }
    };

    const handleSaveServiceTranslations = async (editedTranslations) => {
        setServiceTranslationModalOpen(false);

        if (!translationService) return;

        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            const serviceId = translationService.service_id || translationService.id;
            await fetch(`/api/services/${serviceId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name_translations: editedTranslations.name,
                    description_translations: editedTranslations.description,
                    translations_auto: false,
                }),
            });
            refetchServices();
        } catch (e) {
            console.warn('[service translations] patch error', e);
        }
    };

    const handleOpenCategoryTranslations = async (category) => {
        setTranslationCategory(category);
        setCategoryTranslationModalOpen(true);
        setCategoryTranslationModalLoading(true);
        setCategoryTranslations(null);

        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            const categoryId = category.category_id || category.id;
            const response = await fetch(`/api/categories/${categoryId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setCategoryTranslations({
                    name: data.name_translations || {},
                    description: data.description_translations || {},
                });
            }
        } catch (e) {
            console.warn('[category translations] fetch error', e);
        } finally {
            setCategoryTranslationModalLoading(false);
        }
    };

    const handleSaveCategoryTranslations = async (editedTranslations) => {
        setCategoryTranslationModalOpen(false);

        if (!translationCategory) return;

        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            const categoryId = translationCategory.category_id || translationCategory.id;
            await fetch(`/api/categories/${categoryId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name_translations: editedTranslations.name,
                    description_translations: editedTranslations.description,
                }),
            });
            refetchCategories();
        } catch (e) {
            console.warn('[category translations] patch error', e);
        }
    };

    const toggleCategoryExpansion = (categoryId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFinish = () => {
        if (onQuit) {
            onQuit();
        } else if (onSuccess) {
            onSuccess(t('pro_service_category.categories_services_created_success'));
        }
    };

    const renderCategoriesView = () => {
        const displayCategories = allCategories.slice(0, expandedCategories.showAll ? allCategories.length : 3);
        
        return (
            <Box sx={{ p: { xs: 0, md: 3 }, minHeight: '400px', pb: { xs: 10, md: 3 } }}>
                {allCategories.length > 0 ? (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 3 }, mb: { xs: 2, md: 4 } }}>
                            {displayCategories.map((category, index) => {
                                const categoryId = category.category_id || category.id;
                                const categoryServices = allServices.filter(
                                    service => service.category === categoryId
                                );
                                
                                return (
                                    <Card key={categoryId || index} sx={{ 
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        background: "linear-gradient(145deg, #ffffff 0%, #fdfdfd 100%)",
                                        border: `1px solid ${theme.colors.border.light}`,
                                        borderRadius: "12px",
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        position: "relative",
                                        overflow: "hidden",
                                        "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: "3px",
                                            background: `linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.secondary[300]})`,
                                        },
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: `0 12px 28px rgba(65, 101, 72, 0.12)`,
                                            borderColor: theme.colors.primary[200],
                                            "& .category-actions": {
                                                opacity: 1,
                                                transform: "translateY(0)",
                                            },
                                        },
                                    }}>
                                        <CardContent sx={{
                                            flexGrow: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            padding: { xs: "12px", md: "16px" },
                                            gap: { xs: "8px", md: "12px" },
                                            "&:last-child": {
                                                paddingBottom: { xs: "12px", md: "16px" },
                                            },
                                        }}>
                                            {/* Category Header */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.colors.primary[700],
                                                        fontSize: { xs: "14px", md: "16px" },
                                                        lineHeight: "1.2",
                                                        flexGrow: 1,
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    {getTranslatedField(category, 'name')}
                                                </Typography>
                                                
                                                {/* Action Icons */}
                                                <Box sx={{
                                                    display: "flex",
                                                    gap: "4px",
                                                    alignItems: "center",
                                                    opacity: 1
                                                }}>
                                                    <Tooltip title={t('pro_service_category.add_service_tooltip')} arrow>
                                                        <IconButton
                                                            onClick={() => handleOpenServiceDialog(category)}
                                                            size="small"
                                                            sx={{
                                                                padding: { xs: "4px", md: "6px" },
                                                                borderRadius: "4px",
                                                                backgroundColor: "transparent",
                                                                transition: "all 0.2s ease",
                                                                "&:hover": {
                                                                    backgroundColor: theme.colors.secondary[50],
                                                                    transform: "scale(1.05)",
                                                                },
                                                            }}
                                                        >
                                                            <AddIcon
                                                                sx={{
                                                                    fontSize: { xs: "16px", md: "18px" },
                                                                    color: theme.colors.secondary[600],
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('pro_service_category.edit_category_tooltip')} arrow>
                                                        <IconButton
                                                            onClick={() => handleEditCategory(category)}
                                                            size="small"
                                                            sx={{
                                                                padding: { xs: "4px", md: "6px" },
                                                                borderRadius: "4px",
                                                                backgroundColor: "transparent",
                                                                transition: "all 0.2s ease",
                                                                "&:hover": {
                                                                    backgroundColor: theme.colors.primary[50],
                                                                    transform: "scale(1.05)",
                                                                },
                                                            }}
                                                        >
                                                            <EditIcon
                                                                sx={{
                                                                    fontSize: { xs: "16px", md: "18px" },
                                                                    color: theme.colors.primary[600],
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('pro_service_category.delete_category_tooltip')} arrow>
                                                        <IconButton
                                                            onClick={() => handleDeleteCategory(category)}
                                                            size="small"
                                                            sx={{
                                                                padding: { xs: "4px", md: "6px" },
                                                                borderRadius: "4px",
                                                                backgroundColor: "transparent",
                                                                transition: "all 0.2s ease",
                                                                "&:hover": {
                                                                    backgroundColor: theme.colors.error[50],
                                                                    transform: "scale(1.05)",
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon
                                                                sx={{
                                                                    fontSize: { xs: "16px", md: "18px" },
                                                                    color: theme.colors.error[500],
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                            
                                            {/* Description */}
                                            {category.description && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: theme.colors.text.secondary,
                                                        lineHeight: "1.4",
                                                        fontSize: "13px",
                                                        textAlign: "justify",
                                                        textJustify: "inter-word",
                                                        width: "100%",
                                                        wordBreak: "break-word",
                                                        hyphens: "auto",
                                                        overflow: "hidden",
                                                        display: "-webkit-box",
                                                        WebkitBoxOrient: "vertical",
                                                        WebkitLineClamp: "unset",
                                                        maxHeight: "60px",
                                                        minHeight: "40px",
                                                    }}
                                                >
                                                    {getTranslatedField(category, 'description') || t('pro_service_category.no_description_available')}
                                                </Typography>
                                            )}

                                            {!category.is_default && (
                                                <Button
                                                    onClick={() => handleOpenCategoryTranslations(category)}
                                                    startIcon={<TranslateIcon sx={{ fontSize: '18px !important' }} />}
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{
                                                        mt: 0.5,
                                                        borderRadius: '10px',
                                                        borderColor: theme.colors.secondary[200],
                                                        backgroundColor: theme.colors.secondary[50],
                                                        color: theme.colors.secondary[700],
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.75rem', md: '0.8rem' },
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            borderColor: theme.colors.secondary[300],
                                                            backgroundColor: theme.colors.secondary[100],
                                                        }
                                                    }}
                                                >
                                                    {t('pro_salon.translations.verify_translations', 'Vérifier traduction')}
                                                </Button>
                                            )}
                                            
                                            {/* Services chips */}
                                            {categoryServices.length > 0 && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                    {categoryServices.map((service, serviceIndex) => (
                                                        <Chip
                                                            key={serviceIndex}
                                                            label={`${getTranslatedField(service, 'name')} (${service.price} DA)`}
                                                            sx={{
                                                                backgroundColor: theme.colors.secondary[100],
                                                                color: theme.colors.secondary[700],
                                                                fontSize: '0.8rem'
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                        
                        {/* See More/Less Button */}
                        {allCategories.length > 3 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => toggleCategoryExpansion('showAll')}
                                    startIcon={expandedCategories.showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{
                                        borderColor: theme.colors.primary[300],
                                        color: theme.colors.primary[600],
                                        '&:hover': {
                                            borderColor: theme.colors.primary[500],
                                            backgroundColor: theme.colors.primary[50]
                                        }
                                    }}
                                >
                                    {expandedCategories.showAll ? t('pro_service_category.see_less') : t('pro_service_category.see_more', { count: allCategories.length - 3 })}
                                </Button>
                            </Box>
                        )}
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6, mb: 4 }}>
                        <CategoryIcon sx={{ fontSize: 64, color: theme.colors.primary[300], mb: 2 }} />
                        <Typography variant="h6" sx={{ color: theme.colors.primary[500], mb: 1 }}>
                            {t('pro_service_category.no_category_created')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('pro_service_category.create_categories_first')}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const renderServicesView = () => {
        return (
            <Box sx={{ p: { xs: 0, md: 3 }, minHeight: '400px' }}>
                {hasUnassignedServices && (
                    <Box sx={{ mb: 2 }}>
                        <Alert
                            severity="warning"
                            sx={{
                                backgroundColor: theme.colors.warning[50],
                                color: theme.colors.warning[800],
                                border: `1px solid ${theme.colors.warning[200]}`,
                                fontSize: { xs: '0.8rem', md: '0.875rem' }
                            }}
                        >
                            {t('pro_service_category.unassigned_services_warning')}
                        </Alert>
                    </Box>
                )}
                {allServices.length > 0 ? (
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                        gap: { xs: 1, md: 2 }
                    }}>
                        {allServices.map((service, index) => {
                            const serviceId = service.service_id || service.id;
                            const employeesForService = serviceEmployeesMap[serviceId] || [];
                            
                            return (
                                <Card key={serviceId || index} sx={{ 
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    background: 'linear-gradient(145deg, #ffffff 0%, #fdfdfd 100%)',
                                    border: `1px solid ${theme.colors.border.light}`,
                                    borderRadius: '12px',
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    position: "relative",
                                    overflow: "hidden",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: "3px",
                                        background: `linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.secondary[300]})`,
                                    },
                                    "&:hover": {
                                        transform: 'translateY(-4px)',
                                        boxShadow: `0 12px 28px rgba(65, 101, 72, 0.12)`,
                                        borderColor: theme.colors.primary[200],
                                    },
                                }}>
                                    <CardContent sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        padding: { xs: "12px", md: "16px" },
                                        gap: { xs: "8px", md: "12px" },
                                        "&:last-child": {
                                            paddingBottom: { xs: "12px", md: "16px" },
                                        },
                                    }}>
                                        {/* Service Header with Action Icons */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: theme.colors.primary[700],
                                                    fontSize: { xs: "14px", md: "16px" },
                                                    lineHeight: "1.2",
                                                    flexGrow: 1,
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {getTranslatedField(service, 'name')}
                                            </Typography>
                                            
                                            {/* Action Icons */}
                                            <Box sx={{
                                                display: "flex",
                                                gap: "4px",
                                                alignItems: "center"
                                            }}>
                                                <Tooltip title={t('pro_dashboard.common.edit')} arrow>
                                                    <IconButton
                                                        onClick={() => handleEditService(service)}
                                                        size="small"
                                                        sx={{
                                                            padding: { xs: "4px", md: "6px" },
                                                            borderRadius: "4px",
                                                            backgroundColor: "transparent",
                                                            transition: "all 0.2s ease",
                                                            "&:hover": {
                                                                backgroundColor: theme.colors.primary[50],
                                                                transform: "scale(1.05)",
                                                            },
                                                        }}
                                                    >
                                                        <EditIcon
                                                            sx={{
                                                                fontSize: { xs: "16px", md: "18px" },
                                                                color: theme.colors.primary[600],
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('pro_dashboard.common.delete')} arrow>
                                                    <IconButton
                                                        onClick={() => {
                                                            setSelectedServiceForDelete(service);
                                                            setIsDeleteServiceDialogOpen(true);
                                                        }}
                                                        size="small"
                                                        sx={{
                                                            padding: { xs: "4px", md: "6px" },
                                                            borderRadius: "4px",
                                                            backgroundColor: "transparent",
                                                            transition: "all 0.2s ease",
                                                            "&:hover": {
                                                                backgroundColor: theme.colors.error[50],
                                                                transform: "scale(1.05)",
                                                            },
                                                        }}
                                                    >
                                                        <DeleteIcon
                                                            sx={{
                                                                fontSize: { xs: "16px", md: "18px" },
                                                                color: theme.colors.error[500],
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                        
                                        {/* Service Info */}
                                        {service.description && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.colors.text.secondary,
                                                    lineHeight: "1.4",
                                                    fontSize: { xs: "12px", md: "13px" },
                                                }}
                                            >
                                                {getTranslatedField(service, 'description')}
                                            </Typography>
                                        )}
                                        
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                {service.price && (
                                                    <Typography variant="body2" sx={{ color: theme.colors.success[600], fontWeight: 600, fontSize: { xs: "13px", md: "14px" } }}>
                                                        {service.price} DA
                                                    </Typography>
                                                )}
                                                {service.duration && (
                                                    <Typography variant="body2" sx={{ color: theme.colors.info[600], fontSize: { xs: "12px", md: "13px" } }}>
                                                        {service.duration} min
                                                    </Typography>
                                                )}
                                            </Box>
                                            
                                            <Chip
                                                icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
                                                label={getTranslatedField(service.category_details, 'name') || t('pro_service_category.uncategorized')}
                                                size="small"
                                                sx={{
                                                    background: getTranslatedField(service.category_details, 'name') 
                                                        ? `linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.primary[100]})`
                                                        : `linear-gradient(135deg, ${theme.colors.secondary[50]}, ${theme.colors.secondary[100]})`,
                                                    color: getTranslatedField(service.category_details, 'name') ? theme.colors.primary[700] : theme.colors.secondary[700],
                                                    fontWeight: 600,
                                                    fontSize: { xs: '10px', md: '11px' },
                                                    height: { xs: '20px', md: '22px' },
                                                    '& .MuiChip-icon': {
                                                        color: getTranslatedField(service.category_details, 'name') ? theme.colors.primary[600] : theme.colors.secondary[600]
                                                    }
                                                }}
                                            />
                                        </Box>

                                        <Button
                                            onClick={() => handleOpenTranslations(service)}
                                            startIcon={<TranslateIcon sx={{ fontSize: '18px !important' }} />}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                mt: 1,
                                                borderRadius: '10px',
                                                borderColor: theme.colors.secondary[200],
                                                backgroundColor: theme.colors.secondary[50],
                                                color: theme.colors.secondary[700],
                                                fontWeight: 600,
                                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: theme.colors.secondary[300],
                                                    backgroundColor: theme.colors.secondary[100],
                                                }
                                            }}
                                        >
                                            {t('pro_salon.translations.verify_translations', 'Vérifier traduction')}
                                        </Button>

                                        {employeesForService.length > 0 && (
                                            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: theme.colors.text.secondary,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.3
                                                    }}
                                                >
                                                    {t('pro_service_category.employees')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {employeesForService.map((member, memberIndex) => {
                                                        const userDetails = member.user_details || {};
                                                        const firstName = userDetails.first_name || '';
                                                        const lastName = userDetails.last_name || '';
                                                        const fallbackName = userDetails.full_name || userDetails.username || t('pro_service_category.employee')
                                                        const displayName = (firstName || lastName)
                                                            ? `${firstName} ${lastName}`.trim()
                                                            : fallbackName;

                                                        return (
                                                            <Chip
                                                                key={member.team_member_id || member.id || memberIndex}
                                                                icon={<PersonIcon sx={{ fontSize: 14 }} />}
                                                                label={displayName}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: theme.colors.secondary[100],
                                                                    color: theme.colors.secondary[800],
                                                                    fontSize: '0.7rem',
                                                                    height: 22,
                                                                    '& .MuiChip-icon': {
                                                                        color: theme.colors.secondary[700]
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 8,
                        color: theme.colors.text.secondary 
                    }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.colors.primary[400] }}>
                            {t('pro_service_category.no_service_available')}
                        </Typography>
                        <Typography variant="body2">
                            {t('pro_service_category.add_services_to_start')}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            {/* Main Content */}
            <Card sx={{ 
                mb: { xs: 1, md: 4 }, 
                borderRadius: { xs: 0, md: theme.borderRadius.xl },
                boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
                border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
                backgroundColor: { xs: 'transparent', md: 'white' }
            }}>
                <CardContent sx={{ p: { xs: 1, md: 4 } }}>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            mb: { xs: 2, md: 3 }, 
                            display: 'flex', 
                            alignItems: 'center',
                            color: theme.colors.primary[700],
                            fontWeight: 'bold',
                            textAlign: 'center',
                            justifyContent: 'center',
                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                    >
                        <CategoryIcon sx={{ 
                            mr: { xs: 1, md: 1.5 }, 
                            color: theme.colors.primary[500],
                            fontSize: { xs: '1.5rem', md: '2rem' }
                        }} />
                        {t('pro_service_category.services_title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, justifyContent: 'center', mb: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleOpenCategoryDialog}
                            sx={{
                                backgroundColor: theme.colors.primary[500],
                                color: theme.colors.primary[50],
                                px: { xs: 2, md: 3 },
                                py: { xs: 1, md: 1.5 },
                                borderRadius: theme.borderRadius.lg,
                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                minWidth: { xs: 'auto', md: 'auto' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.5, md: 1 },
                                '&:hover': {
                                    backgroundColor: theme.colors.primary[600],
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <CategoryIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('pro_service_category.add')}</Box>{t('pro_service_category.category')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => handleOpenServiceDialog(null)}
                            sx={{
                                backgroundColor: theme.colors.secondary[500],
                                color: theme.colors.primary[50],
                                px: { xs: 2, md: 3 },
                                py: { xs: 1, md: 1.5 },
                                borderRadius: theme.borderRadius.lg,
                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                minWidth: { xs: 'auto', md: 'auto' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.5, md: 1 },
                                '&:hover': {
                                    backgroundColor: theme.colors.secondary[600],
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <ServiceIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('pro_service_category.add')}</Box>{t('pro_service_category.service')}
                        </Button>
                    </Box>

                    {/* View Toggle Tabs */}
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <Tabs 
                            value={viewMode} 
                            onChange={(e, newValue) => setViewMode(newValue)}
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme.colors.primary[500],
                                },
                                '& .MuiTab-root': {
                                    color: theme.colors.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.colors.primary[600],
                                        fontWeight: 'bold'
                                    }
                                }
                            }}
                        >
                            <Tab 
                                label={<>{t('pro_service_category.categories_upper')}</>}
                                value="categories" 
                                icon={<CategoryIcon />} 
                                iconPosition="start"
                                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                            />
                            <Tab 
                                label={<>{t('pro_service_category.services_upper')}</>}
                                value="services" 
                                icon={<ServiceIcon />} 
                                iconPosition="start"
                                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                            />
                        </Tabs>
                    </Box>

                    {/* Render content based on view mode */}
                    {viewMode === 'categories' ? renderCategoriesView() : renderServicesView()}
                </CardContent>
            </Card>

            {/* Sequential Dialogs - Only one shows at a time */}
            {currentDialog?.type === 'category' && (
                <AddCategory {...currentDialog.props} />
            )}
            
            {currentDialog?.type === 'service' && (
                <AddService {...currentDialog.props} />
            )}

            <TranslationValidationModal
                open={serviceTranslationModalOpen}
                onClose={() => setServiceTranslationModalOpen(false)}
                onValidate={handleSaveServiceTranslations}
                salonName={translationService?.name || ''}
                salonDescription={translationService?.description || ''}
                isLoading={serviceTranslationModalLoading}
                translations={serviceTranslations}
            />

            <TranslationValidationModal
                open={categoryTranslationModalOpen}
                onClose={() => setCategoryTranslationModalOpen(false)}
                onValidate={handleSaveCategoryTranslations}
                salonName={translationCategory?.name || ''}
                salonDescription={translationCategory?.description || ''}
                isLoading={categoryTranslationModalLoading}
                translations={categoryTranslations}
            />

            {/* Edit Service Dialog */}
            {selectedServiceForEdit && (
                <EditService
                    open={isEditServiceDialogOpen}
                    onClose={() => {
                        setIsEditServiceDialogOpen(false);
                        setSelectedServiceForEdit(null);
                    }}
                    onSuccess={handleEditServiceSuccess}
                    serviceData={selectedServiceForEdit}
                />
            )}
            
            {/* Update Category Dialog */}
            {selectedCategoryForEdit && (
                <UpdateCategory
                    open={isUpdateDialogOpen}
                    onClose={() => setIsUpdateDialogOpen(false)}
                    onSuccess={handleUpdateSuccess}
                    categoryData={selectedCategoryForEdit}
                    salonId={selectedSalonId}
                />
            )}
            
            {/* Delete Category Dialog */}
            {selectedCategoryForDelete && (
                <DeleteCategory
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onSuccess={handleDeleteSuccess}
                    categoryData={selectedCategoryForDelete}
                />
            )}
            
            {/* Delete Service Dialog */}
            {selectedServiceForDelete && (
                <DeleteService
                    open={isDeleteServiceDialogOpen}
                    onClose={() => {
                        setIsDeleteServiceDialogOpen(false);
                        setSelectedServiceForDelete(null);
                    }}
                    onSuccess={handleDeleteServiceSuccess}
                    serviceId={selectedServiceForDelete?.id || selectedServiceForDelete?.service_id}
                    serviceName={selectedServiceForDelete?.name}
                    serviceData={selectedServiceForDelete}
                />
            )}
        </>
    );
};

export default ServiceCategorySectionSequential;
